/**
 * CORE-1102, второй заход (QA FAIL 05.09, комментарий 52820) — «ведущая
 * нажимает „ЗАВЕРШИТЬ", появляется „LIVE-СЕССИЯ ЗАВЕРШЕНА", а Chrome на кадрах
 * 07:35–07:37 всё ещё показывает активный индикатор захвата микрофона».
 *
 * Прошлый заход закрыл ту же дыру в LiveKit (`stopLocalMedia.ts`, 5fd102952).
 * Правка верная, но на проде LiveKit выключен: за 01.06–05.09.2026 в
 * `RtcTelemetryEvents` 11 711 строк `provider: agora` и ни одной `livekit`.
 * Гореть могло только на Agora-пути — он здесь.
 *
 * ПОЧЕМУ ОДНОГО `close()` МАЛО.
 *
 * `cleanup()` в `useRoomAgora` снимал дорожки так:
 *
 *     if (audioTrackRef.current) { audioTrackRef.current.close(); … }
 *     if (videoTrackRef.current) { videoTrackRef.current.close(); … }
 *
 * Индикатор захвата в браузере гаснет ровно по `MediaStreamTrack.stop()` — не
 * по mute, не по unpublish и не по выходу из канала. `close()` обёртки SDK до
 * сырой дорожки обычно доходит, но между обёрткой и устройством у нас стоит не
 * одна замена: `setEnabled()` пересоздаёт capture внутри SDK, `setDevice()`
 * переставляет дорожку под той же обёрткой (`device_restored` в хуке зовётся на
 * каждом hotplug), а после unpublish обёртка может уже ничего не держать. И
 * бросок из `close()` в этом коде разбирал весь teardown: он не в try, так что
 * исключение на аудио уносило с собой и видео, и `client.leave()`.
 *
 * Второго шанса на этом пути нет. На «ЗАВЕРШИТЬ» экран НЕ размонтируется —
 * сводка разворачивается ранним возвратом, а хуки живут дальше него, — значит
 * клинап эффекта, который закрыл бы дорожку следом, не выполняется вовсе. Что
 * не погасили здесь, то горит до перезагрузки страницы.
 *
 * Поэтому гасим сами и по всем ссылкам, какие у дорожки есть: обёртку,
 * `getMediaStreamTrack()` и внутреннюю `_originMediaStreamTrack` (после
 * `setEnabled`/`setDevice` это РАЗНЫЕ дорожки, и устройство держит вторая).
 */

/** Всё, что нам нужно от локальной дорожки Agora. Ничего из этого не обязательно. */
interface AgoraLocalTrackLike {
    close?: () => void;
    getMediaStreamTrack?: () => MediaStreamTrack | null | undefined;
    /** Внутреннее поле SDK — исходная дорожка захвата до подмен. */
    _originMediaStreamTrack?: MediaStreamTrack | null;
}

export interface StopAgoraTracksSummary {
    /** Сколько непустых дорожек мы тронули. */
    closed: number;
    /** Сколько сырых дорожек ЕЩЁ держали устройство, когда мы до них дошли.
     *  Это и есть число, которого не хватало в телеметрии: по нему «ушёл чисто»
     *  и «ушёл с живым захватом» перестают быть неразличимы. */
    liveBeforeStop: number;
}

/** Гасит одну сырую дорожку, если она ещё жива. Возвращает, была ли она живой. */
function stopRaw(raw: unknown): boolean {
    const mst = raw as MediaStreamTrack | null | undefined;
    if (!mst || typeof mst.stop !== 'function') return false;
    if (mst.readyState === 'ended') return false;
    try { mst.stop(); } catch { /* уже остановлена */ }
    return true;
}

/**
 * Синхронно снимает локальные дорожки Agora: обёртку SDK и сырые
 * `MediaStreamTrack` под ней.
 *
 * Каждый шаг в своём try — teardown обязан дойти до последней дорожки и до
 * `client.leave()` даже если SDK бросил на первой.
 */
export function stopAgoraLocalTracks(tracks: Array<unknown>): StopAgoraTracksSummary {
    let closed = 0;
    let liveBeforeStop = 0;
    for (const entry of tracks) {
        if (!entry) continue;
        closed += 1;
        const track = entry as AgoraLocalTrackLike;
        let current: unknown = null;
        // Снимок ДО close(): после него getMediaStreamTrack() у части версий SDK
        // отдаёт null, и гасить было бы уже нечего.
        try { current = track.getMediaStreamTrack?.(); } catch { /* нет метода */ }
        const origin = track._originMediaStreamTrack ?? null;
        try { track.close?.(); } catch { /* уже закрыта — не повод бросать teardown */ }
        if (stopRaw(current)) liveBeforeStop += 1;
        if (origin && origin !== current && stopRaw(origin)) liveBeforeStop += 1;
    }
    return { closed, liveBeforeStop };
}

/**
 * Принять только что созданную локальную дорожку — или погасить её, если
 * сессия кончилась, пока она создавалась.
 *
 * `AgoraRTC.createMicrophoneAudioTrack()` / `createCameraVideoTrack()`
 * ждут разрешения пользователя и ответа устройства — это сотни миллисекунд, в
 * которые пользователь вполне успевает нажать «ЗАВЕРШИТЬ». Раньше результат
 * присваивался в ссылку безусловно, уже ПОСЛЕ того как `cleanup()` её обнулил.
 * Такую дорожку не закрывал никто: `cleanup()` до неё не дожил, а клинапа
 * эффекта на этом пути нет (экран не размонтируется). Микрофон оставался
 * захваченным поверх «LIVE-СЕССИЯ ЗАВЕРШЕНА».
 *
 * @param stale сессия успела кончиться, пока дорожка создавалась
 * @returns была ли дорожка принята
 */
export function adoptOrStopLocalTrack<T>(track: T, stale: boolean, assign: (track: T) => void): boolean {
    if (stale) {
        stopAgoraLocalTracks([track]);
        return false;
    }
    assign(track);
    return true;
}
